import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import testimonials from "@/data/testimonials.json";
import { OFFICE_SAVINGS, officeSavings, formatSavingsHours, formatSavingsMoney } from "@/lib/officeSavings";
import {
  Microphone,
  Stop,
  Waveform,
  FileText,
  Receipt,
  Users,
  Buildings,
  Package,
  CreditCard,
  EnvelopeSimple,
  ChartLineUp,
  ArrowsClockwise,
  Camera,
  Sparkle,
  CheckCircle,
  AppleLogo,
  GooglePlayLogo,
  CaretDown,
  ArrowRight,
  Lightning,
  Trophy,
  Wallet,
  MagnifyingGlass,
  Clock,
  HardHat,
  CalendarBlank,
  Bell,
  Signature,
} from "@phosphor-icons/react";

const API_BASE = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api`;
const APP_ORIGIN = (process.env.REACT_APP_APP_URL || "https://jobspoken-web.onrender.com").replace(/\/$/, "");
const REGISTER_URL = `${APP_ORIGIN}/register`;
const LOGIN_URL = `${APP_ORIGIN}/login`;

const SAMPLE_JOBS = [
  {
    id: "electrical",
    label: "Electrical panel",
    text: "Replaced a 200-amp electrical panel and four outlets at the Miller house. Materials plus labor, fourteen fifty total.",
  },
  {
    id: "plumbing",
    label: "Emergency pipe repair",
    text: "Emergency pipe repair in the basement for the Garcias, two hours of labor plus parts, three eighty.",
  },
  {
    id: "hvac",
    label: "HVAC service",
    text: "HVAC capacitor replacement and refrigerant refill on the rooftop unit at Daily Double, six twenty all in.",
  },
];

const VOICE_TO_PAID = [
  {
    n: "01",
    title: "Speak the job",
    body: "Hit the mic from the driveway or type it out. Plain trade talk is fine — panel swap, clogged main, capacitor change.",
  },
  {
    n: "02",
    title: "AI maps materials & labor",
    body: "JobSpoken picks out the tasks, hours, and the materials or supplies needed to finish the work you described.",
  },
  {
    n: "03",
    title: "Prices fill in automatically",
    body: "Right after you speak, live retailer lookups and AI labor estimates fill the draft — labeled Live lookup or Estimated. Everything stays editable.",
  },
  {
    n: "04",
    title: "Send a professional estimate",
    body: "Share EST-#### for the client to review and e-sign from their phone. One tap turns a signed estimate into a branded invoice.",
  },
  {
    n: "05",
    title: "Win it, schedule it",
    body: "When they sign, you get an instant job-won email plus an in-app alert. Drop the job on the built-in calendar and add it to yours with .ics.",
  },
  {
    n: "06",
    title: "Get paid faster",
    body: "From signed estimate to invoice in one tap. Card payment links with optional tips, cash/check on site, and past-due reminders.",
  },
];

const WIN_JOBS = [
  {
    icon: Lightning,
    title: "Quote while you're still on site",
    body: "Speak the scope before you leave. Prices fill in automatically so you can answer with a number, not a maybe.",
  },
  {
    icon: Signature,
    title: "Estimates clients can e-sign",
    body: "Send a professional estimate. Clients review and sign from their phone — you get a signed PDF and audit trail.",
  },
  {
    icon: Bell,
    title: "Know the second you won it",
    body: "A signed estimate triggers an instant job-won email and an in-app alert so you can schedule before the next call.",
  },
  {
    icon: CalendarBlank,
    title: "Schedule the won job",
    body: "Day, week, and list views plus a next-open-slot suggestion. Add the job to your calendar with one tap (.ics).",
  },
];

const DO_THE_WORK = [
  {
    icon: Receipt,
    title: "Receipts become real job cost",
    body: "Snap a supplier receipt. OCR splits the lines so you assign materials to the job and see true cost.",
  },
  {
    icon: Package,
    title: "Van stock that stays honest",
    body: "On-hand quantities, returns, job usage, and a ledger — know what left the truck and what to reorder.",
  },
  {
    icon: Camera,
    title: "Before/after proof on the job",
    body: "Attach photos to the work so the invoice tells the full story when a homeowner asks.",
  },
  {
    icon: ArrowsClockwise,
    title: "Recurring work on autopilot",
    body: "Turn a job into a recurring template — filters, tune-ups, seasonal visits — with upcoming drafts on Home.",
  },
];

const GET_PAID = [
  {
    icon: CreditCard,
    title: "Payment links & tips",
    body: "Share a Stripe link by email or SMS. Clients pay by card and can add a tip — cash collected shows on Home.",
  },
  {
    icon: EnvelopeSimple,
    title: "Email, SMS, or hand it over",
    body: "Send the invoice with a share link and PDF. Record cash or check on site when they pay in person.",
  },
  {
    icon: Clock,
    title: "Past-due reminders",
    body: "Friendly reminder emails go out on outstanding balances so you aren't chasing every invoice by hand.",
  },
  {
    icon: ChartLineUp,
    title: "See cash in vs still out",
    body: "Job profit after materials, monthly collected vs outstanding, and tips by day, week, or month.",
  },
];

const FEATURES = [
  {
    icon: Microphone,
    title: "Voice job intake",
    body: "Speak once. AI extracts tasks, hours, materials, and billables into an editable job draft.",
  },
  {
    icon: MagnifyingGlass,
    title: "Automatic pricing",
    body: "Live retailer lookups and AI estimates fill prices right after you speak — labeled Live lookup or Estimated, always editable.",
  },
  {
    icon: Signature,
    title: "Estimates & e-signature",
    body: "Send EST-#### for clients to review and e-sign on their phone. Signed PDF plus audit trail included.",
  },
  {
    icon: Bell,
    title: "Job-won alerts",
    body: "When a client signs, you get an email and an in-app banner so you can schedule the work immediately.",
  },
  {
    icon: CalendarBlank,
    title: "Built-in schedule",
    body: "Day, week, and list views, next-open-slot suggestions, and add-to-calendar (.ics) for won jobs.",
  },
  {
    icon: FileText,
    title: "Branded PDF invoices",
    body: "One tap from a signed estimate to invoice. Your logo on every bill — simple, standard, or detailed.",
  },
  {
    icon: CreditCard,
    title: "Payment links & tips",
    body: "Share a link by email or SMS. Clients pay by card with optional tip — cash collected on Home.",
  },
  {
    icon: Receipt,
    title: "Receipt scanning",
    body: "Photograph a supplier receipt. OCR splits line items so you assign materials and track true cost.",
  },
  {
    icon: Users,
    title: "Clients & history",
    body: "Customer contacts, past invoices, tips, and balances — searchable from the truck.",
  },
  {
    icon: Buildings,
    title: "Properties & equipment",
    body: "Track service addresses, equipment on site, photos, and a timeline of every visit.",
  },
  {
    icon: Package,
    title: "Stock / inventory",
    body: "On-hand quantities, purchase returns, job usage, and a ledger so van stock stays honest.",
  },
  {
    icon: ArrowsClockwise,
    title: "Recurring work",
    body: "Turn any invoice into a recurring template with upcoming work surfaced on Home.",
  },
  {
    icon: ChartLineUp,
    title: "Profitability & cash flow",
    body: "See job profit after materials, monthly collected vs outstanding, and tips by period.",
  },
  {
    icon: Camera,
    title: "Job photos",
    body: "Attach before/after proof of work so the invoice tells the full story.",
  },
];

const FAQS = [
  {
    q: "Do I need a credit card for the trial?",
    a: "No. New accounts get a 14-day free trial with no payment method. The paywall appears only after the trial ends.",
  },
  {
    q: "What trades does voice understanding cover?",
    a: "Plumbing, electrical, HVAC, carpentry, and general repair language are first-class. Speak naturally — rough-in, change order, T&M all work.",
  },
  {
    q: "Does it price materials and labor for me?",
    a: "Yes. Right after you speak the job, prices fill in automatically — live retailer lookups where available, otherwise AI estimates — each labeled Live lookup or Estimated. You can edit every line before you send.",
  },
  {
    q: "Can clients e-sign an estimate on their phone?",
    a: "Yes. Share a professional estimate link. Clients review the scope and totals, draw their signature, and accept. You get a signed PDF with an audit trail, plus an instant job-won email and in-app alert.",
  },
  {
    q: "Can I schedule the job after they accept?",
    a: "Yes. Won jobs show up on a built-in calendar (day, week, and list). Suggest the next open slot, then download an .ics to add it to the calendar you already use.",
  },
  {
    q: "Can clients pay from the invoice?",
    a: "Yes. One tap turns a signed estimate into an invoice with a share link. Clients can pay by card (Stripe) and optionally add a tip. You can also record cash or check on site, and past-due reminders follow up for you.",
  },
  {
    q: "Is there an iPhone or Android app?",
    a: "The web app works in mobile browsers today. Native App Store and Google Play builds are coming soon.",
  },
  {
    q: "Can I try one estimate before I create an account?",
    a: "Yes. This page includes one free estimate per browser. Speak or type a job and you'll see a priced preview. It is not saved and it is not sent to a client. After that, start the 14-day free trial.",
  },
  {
    q: "What does it cost after the trial?",
    a: "Job Spoken Pro is $14.99/month or $119.99/year. Founding beta pricing, when a founding code is on the account, is $9.99/month or $99.99/year and stays while the subscription renews. Cancel anytime from Settings or your store account.",
  },
];

const FREE_ESTIMATE_KEY = "jobspoken.freeEstimate.used";

const TRADE_PHOTOS = [
  {
    src: "/images/trades/contractor-phone.webp",
    width: 1400,
    height: 788,
    alt: "Contractor in work clothes crouching at a job site and holding a smartphone",
    label: "Contractors",
  },
  {
    src: "/images/trades/electrician.webp",
    width: 1054,
    height: 1400,
    alt: "Electrician in a hard hat working inside an electrical cabinet",
    label: "Electricians",
  },
  {
    src: "/images/trades/plumber.webp",
    width: 1050,
    height: 1400,
    alt: "Tradesperson in a hard hat and safety vest holding a plumber's wrench",
    label: "Plumbers",
  },
  {
    src: "/images/trades/painter.webp",
    width: 1400,
    height: 934,
    alt: "Painter in a hard hat holding a roller on an indoor job site",
    label: "Painters",
  },
  {
    src: "/images/trades/painter-phone.webp",
    width: 1400,
    height: 934,
    alt: "Person painting a wall and looking at a smartphone",
    label: "On the phone",
    position: "object-[78%_center]",
  },
  {
    src: "/images/trades/landscaper.webp",
    width: 1400,
    height: 934,
    alt: "Lawn-care operator kneeling beside a mower in a yard",
    label: "Landscapers",
  },
  {
    src: "/images/trades/shop-owner-phone.webp",
    width: 1400,
    height: 788,
    alt: "Small shop owner talking on a phone beside rolls of material",
    label: "Shop owners",
  },
];

const CLIENT_STEPS = [
  {
    n: "01",
    title: "They get the estimate by link",
    body: "You text or email a link. The homeowner opens the scope and the total in the browser. There is no app to install.",
  },
  {
    n: "02",
    title: "They sign on their phone",
    body: "They review the lines, draw a signature, and accept. That takes seconds. You get a signed PDF and an audit trail.",
  },
  {
    n: "03",
    title: "The job gets a date",
    body: "You put the won job on the built-in calendar and can send an .ics file they can add to the calendar they already use.",
  },
  {
    n: "04",
    title: "They pay by card",
    body: "The invoice link takes a card payment, with an optional tip. If they pay cash or check on site, you record it there.",
  },
];

function readFreeEstimateUsed() {
  try {
    return window.localStorage.getItem(FREE_ESTIMATE_KEY) === "1";
  } catch {
    return false;
  }
}

function markFreeEstimateUsed() {
  try {
    window.localStorage.setItem(FREE_ESTIMATE_KEY, "1");
  } catch {
    /* Private mode still stops a second try for this page session. */
  }
}

function isTestimonial(item) {
  if (!item || typeof item !== "object") return false;
  if (typeof item.quote !== "string" || !item.quote.trim()) return false;
  if (typeof item.name !== "string" || !item.name.trim()) return false;
  if (item.type !== "contractor" && item.type !== "client") return false;
  return true;
}

function money(n) {
  return `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function LogoMark({ className = "h-9 w-9" }) {
  return (
    <div className={`${className} rounded-lg bg-amber-500 flex items-end justify-center gap-[3px] pb-2 pt-2 px-2`}>
      <span className="w-[3px] h-2 bg-[#0B0C0E] rounded-full" />
      <span className="w-[3px] h-4 bg-[#0B0C0E] rounded-full" />
      <span className="w-[3px] h-3 bg-[#0B0C0E] rounded-full" />
    </div>
  );
}

function LiveVoiceDemo() {
  const [mode, setMode] = useState("idle"); // idle | recording | working | done | error
  const [text, setText] = useState(SAMPLE_JOBS[0].text);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState("");
  const [used, setUsed] = useState(readFreeEstimateUsed);
  const usedRef = useRef(used);
  const inflightRef = useRef(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const lockFreeTry = () => {
    usedRef.current = true;
    markFreeEstimateUsed();
    setUsed(true);
  };

  const parseTranscript = async (transcript) => {
    if (usedRef.current || inflightRef.current) return;
    inflightRef.current = true;
    setMode("working");
    setError("");
    setDraft(null);
    try {
      const { data } = await axios.post(`${API_BASE}/demo/parse`, { transcript }, { timeout: 60000 });
      setDraft(data);
      setMode("done");
      lockFreeTry();
    } catch (e) {
      if (e.response?.status === 429) {
        lockFreeTry();
        setMode("done");
        return;
      }
      const detail = e.response?.data?.detail || e.message || "Demo failed.";
      setError(typeof detail === "string" ? detail : "Demo failed.");
      setMode("error");
    } finally {
      inflightRef.current = false;
    }
  };

  const startMic = async () => {
    if (usedRef.current) return;
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 800) {
          setError("Recording too short — hold the mic a second longer, or type the job.");
          setMode("idle");
          return;
        }
        setMode("working");
        try {
          const fd = new FormData();
          fd.append("audio", blob, "demo.webm");
          const tr = await axios.post(`${API_BASE}/demo/transcribe`, fd, { timeout: 60000 });
          const spoken = (tr.data.text || "").trim();
          if (!spoken) throw new Error("Didn't catch that.");
          setText(spoken);
          await parseTranscript(spoken);
        } catch (e) {
          if (e.response?.status === 429) {
            lockFreeTry();
            setMode("done");
            return;
          }
          const detail = e.response?.data?.detail || e.message || "Couldn't process that recording.";
          setError(typeof detail === "string" ? detail : "Couldn't process that recording.");
          setMode("error");
        }
      };
      mr.start();
      mediaRef.current = mr;
      setMode("recording");
    } catch {
      setError("Microphone access denied. Type the job below instead.");
      setMode("idle");
    }
  };

  const stopMic = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
  };

  return (
    <div
      id="demo"
      data-testid="live-voice-demo"
      className="relative rounded-2xl border border-white/10 bg-[#13151A]/95 p-5 sm:p-6 shadow-[0_24px_80px_-20px_rgba(0,0,0,0.7)]"
    >
      <div className="flex items-center justify-between gap-3 mb-4">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-400">Try one free estimate</span>
        <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
          <span className={`h-1.5 w-1.5 rounded-full ${mode === "recording" ? "bg-amber-400 animate-pulse" : mode === "working" ? "bg-sky-400 animate-pulse" : "bg-emerald-400"}`} />
          {mode === "recording" ? "Listening…" : mode === "working" ? "Building estimate…" : used ? "Used" : "Ready"}
        </span>
      </div>

      {used && !draft ? (
        <div data-testid="demo-free-try-used">
          <h2 className="font-heading text-2xl font-bold tracking-tight">You&apos;ve used your free estimate.</h2>
          <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
            That preview is one per browser. Start the 14-day free trial to speak the next job, send an estimate the client can e-sign, and get paid.
          </p>
          <a
            href={REGISTER_URL}
            data-testid="demo-cta-signup"
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-amber-500 py-3 font-heading text-sm font-bold text-[#0B0C0E] hover:bg-amber-400 transition-colors"
          >
            Start 14-day free trial <ArrowRight size={16} weight="bold" />
          </a>
        </div>
      ) : (
        <>
      {!used && (
        <>
      <div className="flex flex-wrap gap-2 mb-4">
        {SAMPLE_JOBS.map((s) => (
          <button
            key={s.id}
            type="button"
            data-testid={`demo-sample-${s.id}`}
            onClick={() => { setText(s.text); setDraft(null); setError(""); setMode("idle"); }}
            className="rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 text-xs font-medium text-zinc-400 hover:border-amber-500/40 hover:text-amber-300 transition-colors"
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="flex items-start gap-3 mb-4">
        <button
          type="button"
          data-testid="demo-mic-button"
          aria-label={mode === "recording" ? "Stop recording" : "Record with microphone"}
          onClick={mode === "recording" ? stopMic : startMic}
          disabled={mode === "working"}
          className={`relative flex h-14 w-14 shrink-0 items-center justify-center rounded-full transition-colors disabled:opacity-50 ${
            mode === "recording" ? "bg-red-500 text-white" : "bg-amber-500 text-[#0B0C0E] hover:bg-amber-400"
          }`}
        >
          {mode === "recording" && <span className="absolute inset-0 rounded-full bg-red-500/40 animate-ping" />}
          {mode === "working" ? <Waveform size={22} weight="fill" className="animate-pulse" /> : mode === "recording" ? <Stop size={22} weight="fill" /> : <Microphone size={22} weight="fill" />}
        </button>
        <textarea
          data-testid="demo-transcript-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          placeholder="Or type a job: “Swapped the garbage disposal at the Jones place, parts from Lowe’s…”"
          className="flex-1 rounded-xl border border-white/10 bg-[#0B0C0E] px-3 py-2.5 text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500/40 resize-y min-h-[5.5rem]"
        />
      </div>

      <button
        type="button"
        data-testid="demo-parse-button"
        disabled={mode === "working" || mode === "recording" || text.trim().length < 8}
        onClick={() => parseTranscript(text.trim())}
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg bg-amber-500 px-5 py-2.5 font-heading text-sm font-bold text-[#0B0C0E] hover:bg-amber-400 disabled:opacity-40 transition-colors"
      >
        Build my free estimate <ArrowRight size={16} weight="bold" />
      </button>
        </>
      )}

      {error && (
        <p data-testid="demo-error" className="mt-4 text-sm text-red-400">{error}</p>
      )}

      {draft && (
        <div data-testid="demo-invoice-preview" className="mt-5 rounded-xl border border-white/10 bg-[#0B0C0E] p-4">
          <div className="flex items-center justify-between mb-3 gap-3">
            <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              <FileText size={12} className="text-amber-400" /> Estimate preview
            </span>
            <span className="rounded-full bg-emerald-500/15 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-emerald-400">
              One free try
            </span>
          </div>
          {(draft.customer_name || draft.location) && (
            <p className="text-xs text-zinc-500 mb-2">
              {[draft.customer_name, draft.location].filter(Boolean).join(" · ")}
              {draft.date ? ` · ${draft.date}` : ""}
            </p>
          )}
          {draft.interpretation && (
            <p className="text-sm text-zinc-400 italic mb-3">“{draft.interpretation}”</p>
          )}
          <div className="space-y-2">
            {draft.line_items.map((li, i) => (
              <div key={`${li.description}-${i}`} className="flex items-center justify-between text-sm gap-3">
                <span className="text-zinc-300">
                  {li.description}{" "}
                  <span className="text-zinc-600">× {li.qty}</span>
                </span>
                <span className="font-mono text-zinc-200 shrink-0">{money(li.amount)}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 border-t border-dashed border-white/10 pt-3 space-y-1.5">
            <div className="flex justify-between text-xs text-zinc-500">
              <span>Subtotal</span>
              <span className="font-mono">{money(draft.subtotal)}</span>
            </div>
            {draft.tax_rate > 0 && (
              <div className="flex justify-between text-xs text-zinc-500">
                <span>Tax ({draft.tax_rate}%)</span>
                <span className="font-mono">{money(draft.tax)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm font-semibold text-zinc-50 pt-1">
              <span>Estimate total</span>
              <span className="font-mono text-amber-400">{money(draft.total)}</span>
            </div>
          </div>
          <p className="mt-3 text-xs text-zinc-500 leading-relaxed">
            That&apos;s your one free estimate on this browser. Nothing was saved or sent. In the app this becomes an estimate the client can e-sign.
          </p>
          <a
            href={REGISTER_URL}
            data-testid="demo-cta-signup"
            className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-amber-500 py-2.5 font-heading text-sm font-bold text-[#0B0C0E] hover:bg-amber-400 transition-colors"
          >
            Start 14-day free trial
          </a>
        </div>
      )}

      <p className="mt-4 text-[11px] text-zinc-600 leading-relaxed">
        One free estimate per browser. The server also limits demo requests per network. Nothing is saved to your books.
      </p>
        </>
      )}
    </div>
  );
}

function FaqItem({ item, open, onToggle }) {
  return (
    <div className="border-b border-white/10">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-4 py-4 text-left"
        aria-expanded={open}
      >
        <span className="font-heading text-base font-semibold text-zinc-100">{item.q}</span>
        <CaretDown size={18} className={`text-zinc-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="pb-4 text-sm text-zinc-400 leading-relaxed">{item.a}</p>}
    </div>
  );
}

function BenefitCard({ item }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#13151A]/80 p-5 hover:border-amber-500/40 transition-colors">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/15 text-amber-400 mb-4">
        <item.icon size={20} weight="bold" />
      </div>
      <h3 className="font-heading text-lg font-semibold text-zinc-50">{item.title}</h3>
      <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{item.body}</p>
    </div>
  );
}

function TradeGallery() {
  return (
    <section id="on-the-job" className="relative border-t border-white/5 py-16 sm:py-24" data-testid="trade-photos">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400 mb-4">01 — On the job</p>
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight max-w-2xl">
          Built for people who quote from the driveway.
        </h2>
        <p className="mt-4 max-w-2xl text-sm sm:text-base text-zinc-400 leading-relaxed">
          Contractors, electricians, plumbers, painters, landscapers, and small shop owners — speak the job on the phone you already carry.
        </p>
        <div className="mt-8 flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 lg:grid lg:grid-cols-4 lg:overflow-visible">
          {TRADE_PHOTOS.map((photo) => (
            <figure key={photo.src} className="w-[78%] shrink-0 snap-start sm:w-[46%] lg:w-auto">
              <div className="aspect-[4/5] overflow-hidden rounded-2xl border border-white/10 bg-[#13151A]">
                <img
                  src={photo.src}
                  alt={photo.alt}
                  width={photo.width}
                  height={photo.height}
                  loading="lazy"
                  decoding="async"
                  className={`h-full w-full object-cover ${photo.position || "object-center"}`}
                />
              </div>
              <figcaption className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">{photo.label}</figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

function SavingsSection() {
  const [estimatesPerMonth, setEstimatesPerMonth] = useState(OFFICE_SAVINGS.estimatesPerMonth);
  const [hourlyRate, setHourlyRate] = useState(OFFICE_SAVINGS.hourlyRate);
  const model = officeSavings({ estimatesPerMonth, hourlyRate });
  const typical = estimatesPerMonth === OFFICE_SAVINGS.estimatesPerMonth && hourlyRate === OFFICE_SAVINGS.hourlyRate;

  return (
    <section id="savings" className="relative border-t border-white/5 py-20 sm:py-28" data-testid="savings-section">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500/15 text-amber-400">
            <Clock size={18} weight="bold" />
          </div>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400">03 — Time and money</p>
        </div>
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight max-w-2xl">
          Estimated office hours and overhead for a typical small contractor.
        </h2>
        <p className="mt-4 max-w-2xl text-sm sm:text-base text-zinc-400 leading-relaxed">
          These figures are a planning estimate. They are not measured results from JobSpoken users — the product has not launched, so there is no customer data behind them.
        </p>

        <div className="mt-8 grid sm:grid-cols-2 gap-4 max-w-3xl">
          <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-400">Estimated office time saved</p>
            <p className="mt-2 font-heading text-4xl font-extrabold" data-testid="savings-hours">
              {formatSavingsHours(model.hours)}
              <span className="text-lg text-zinc-400 font-semibold"> hrs/mo</span>
            </p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#13151A] p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Estimated overhead saved</p>
            <p className="mt-2 font-heading text-4xl font-extrabold" data-testid="savings-dollars">
              {formatSavingsMoney(model.dollars)}
              <span className="text-lg text-zinc-400 font-semibold"> /mo</span>
            </p>
          </div>
        </div>

        <div className="mt-8 max-w-3xl rounded-2xl border border-white/10 bg-[#13151A]/80 p-5">
          <p className="text-sm text-zinc-300">
            {typical ? "Showing the typical small contractor." : "Adjusted from the typical small contractor. Invoices and scheduling stay on the baseline."}
          </p>
          <div className="mt-5 grid sm:grid-cols-2 gap-6">
            <label className="block">
              <span className="flex items-center justify-between text-sm text-zinc-300">
                Estimates per month
                <span className="font-mono text-amber-400" data-testid="savings-estimates-value">{estimatesPerMonth}</span>
              </span>
              <input
                data-testid="savings-estimates-slider"
                type="range"
                min={4}
                max={60}
                step={1}
                value={estimatesPerMonth}
                onChange={(e) => setEstimatesPerMonth(Number(e.target.value))}
                className="mt-3 w-full accent-amber-500 h-8"
              />
            </label>
            <label className="block">
              <span className="flex items-center justify-between text-sm text-zinc-300">
                Value of an office hour
                <span className="font-mono text-amber-400" data-testid="savings-rate-value">${hourlyRate}</span>
              </span>
              <input
                data-testid="savings-rate-slider"
                type="range"
                min={20}
                max={80}
                step={1}
                value={hourlyRate}
                onChange={(e) => setHourlyRate(Number(e.target.value))}
                className="mt-3 w-full accent-amber-500 h-8"
              />
            </label>
          </div>
          {!typical && (
            <button
              type="button"
              data-testid="savings-reset"
              onClick={() => {
                setEstimatesPerMonth(OFFICE_SAVINGS.estimatesPerMonth);
                setHourlyRate(OFFICE_SAVINGS.hourlyRate);
              }}
              className="mt-4 text-sm font-semibold text-amber-400"
            >
              Reset to typical
            </button>
          )}

          <details className="mt-5 border-t border-white/10 pt-4" data-testid="savings-assumptions">
            <summary className="cursor-pointer font-heading text-sm font-semibold text-zinc-100">How we calculate this</summary>
            <div className="mt-3 space-y-2 text-sm text-zinc-400 leading-relaxed">
              <p>
                Estimates: {model.estimatesPerMonth} per month × ({model.estimateMinutesByHand} min by hand − {model.estimateMinutesWithApp} min with JobSpoken)
                = {model.estimateMinutesSaved} min = {formatSavingsHours(model.estimateHours)} hours.
              </p>
              <p>
                Invoices: {model.invoicesPerMonth} per month × ({model.invoiceMinutesByHand} min by hand − {model.invoiceMinutesWithApp} min with JobSpoken)
                = {model.invoiceMinutesSaved} min = {formatSavingsHours(model.invoiceHours)} hours.
              </p>
              <p>
                Scheduling and follow-up: {formatSavingsHours(model.schedulingHoursSaved)} hours a month. This stays fixed when you move the sliders.
              </p>
              <p>
                Total office time: {formatSavingsHours(model.estimateHours)} + {formatSavingsHours(model.invoiceHours)} + {formatSavingsHours(model.schedulingHoursSaved)} = {formatSavingsHours(model.hours)} hours.
                At ${model.hourlyRate}/hour that is {formatSavingsMoney(model.dollars)} (nearest dollar).
              </p>
              <p>
                Office time is valued as overhead you would otherwise spend at a desk, not as extra jobs sold. No study and no JobSpoken customer average is behind these numbers.
              </p>
            </div>
          </details>
        </div>
      </div>
    </section>
  );
}

function ClientExperience() {
  return (
    <section id="for-clients" className="relative border-t border-white/5 py-20 sm:py-28" data-testid="client-experience">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400 mb-4">07 — For the homeowner</p>
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight max-w-2xl">
          What your client actually does.
        </h2>
        <p className="mt-4 max-w-2xl text-sm sm:text-base text-zinc-400 leading-relaxed">
          This is how the product works for the person who hired you. It is a description of the flow, not a quote from a customer.
        </p>
        <div className="mt-10 grid sm:grid-cols-2 gap-4">
          {CLIENT_STEPS.map((step) => (
            <div key={step.n} className="rounded-2xl border border-white/10 bg-[#13151A]/80 p-5">
              <div className="font-mono text-amber-400 text-sm tracking-[0.2em] mb-3">{step.n}</div>
              <h3 className="font-heading text-lg font-semibold text-zinc-50">{step.title}</h3>
              <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{step.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function VoicesSection() {
  const items = (Array.isArray(testimonials) ? testimonials : []).filter(isTestimonial);
  if (items.length === 0) {
    return (
      <section id="voices" className="relative border-t border-white/5 py-16 sm:py-20" data-testid="founding-members">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400 mb-4">09 — Early access</p>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Founding members wanted</h2>
          <p className="mt-4 text-sm sm:text-base text-zinc-400 leading-relaxed">
            There are no reviews yet. JobSpoken has not launched and has no customers to quote.
            Early contractors can join the beta at founding pricing: <span className="text-zinc-100">$9.99/month or $99.99/year</span>,
            kept for as long as the subscription renews. Start a 14-day free trial with no card.
            If you have a founding code, enter it on the signup form — that is what puts founding pricing on the account.
          </p>
          <a
            href={REGISTER_URL}
            data-testid="founding-cta"
            className="mt-6 inline-flex rounded-xl bg-amber-500 px-6 py-3.5 font-heading text-sm font-bold text-[#0B0C0E] hover:bg-amber-400"
          >
            Join the beta
          </a>
        </div>
      </section>
    );
  }

  return (
    <section id="voices" className="relative border-t border-white/5 py-20 sm:py-28" data-testid="testimonials-section">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400 mb-4">09 — From the field</p>
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">What contractors and their clients say</h2>
        <div className="mt-10 grid sm:grid-cols-2 gap-4">
          {items.map((item) => {
            const who = [item.name, item.role, item.business, item.city].filter(Boolean).join(" · ");
            const rating = Number(item.rating);
            return (
              <figure key={`${item.type}-${item.name}-${item.quote.slice(0, 24)}`} className="rounded-2xl border border-white/10 bg-[#13151A]/80 p-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-amber-400">
                  {item.type === "client" ? "Client" : "Contractor"}
                  {Number.isFinite(rating) ? ` · ${rating} / 5` : ""}
                </p>
                <blockquote className="mt-3 text-sm text-zinc-200 leading-relaxed">“{item.quote}”</blockquote>
                <figcaption className="mt-3 text-xs text-zinc-500">{who}</figcaption>
              </figure>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(0);
  const year = useMemo(() => new Date().getFullYear(), []);

  useEffect(() => {
    document.title = "Job Spoken – Voice Invoicing for Trade Pros";
    document.documentElement.classList.add("landing-root");
    return () => {
      document.documentElement.classList.remove("landing-root");
      document.title = "Job Spoken";
    };
  }, []);

  const nav = [
    { label: "How it works", href: "#how-it-works" },
    { label: "Time saved", href: "#savings" },
    { label: "Features", href: "#features" },
    { label: "Free estimate", href: "#demo" },
    { label: "Pricing", href: "#pricing" },
    { label: "FAQ", href: "#faq" },
  ];

  return (
    <div className="landing min-h-screen bg-[#0B0C0E] text-zinc-100" data-testid="landing-page">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(245,158,11,0.12),_transparent_55%)]" />
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.035]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      <header className="sticky top-0 z-40 border-b border-white/5 bg-[#0B0C0E]/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-3.5 sm:px-8">
          <a href="#top" className="flex items-center gap-2.5" data-testid="landing-logo">
            <LogoMark />
            <div>
              <div className="font-heading text-sm font-extrabold tracking-wide uppercase">Job Spoken</div>
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">jobspoken.com</div>
            </div>
          </a>
          <nav className="hidden lg:flex items-center gap-5">
            {nav.map((n) => (
              <a key={n.href} href={n.href} className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 hover:text-amber-400 transition-colors">
                {n.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <a
              href="#demo"
              data-testid="nav-try-voice-button"
              className="hidden sm:inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/10 px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] text-amber-400 hover:bg-amber-500 hover:text-[#0B0C0E] transition-colors"
            >
              <Microphone size={14} weight="fill" /> Try one free estimate
            </a>
            <a
              href={REGISTER_URL}
              className="hidden sm:inline-flex rounded-full bg-amber-500 px-4 py-2 font-heading text-xs font-bold text-[#0B0C0E] hover:bg-amber-400 transition-colors"
            >
              Start free
            </a>
            <button
              type="button"
              className="lg:hidden text-zinc-300 p-2"
              aria-label="Toggle menu"
              onClick={() => setMenuOpen((v) => !v)}
            >
              <span className="block w-5 h-0.5 bg-current mb-1.5" />
              <span className="block w-5 h-0.5 bg-current mb-1.5" />
              <span className="block w-5 h-0.5 bg-current" />
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="lg:hidden border-t border-white/5 px-5 py-4 space-y-3 bg-[#0B0C0E]">
            {nav.map((n) => (
              <a key={n.href} href={n.href} onClick={() => setMenuOpen(false)} className="block font-mono text-xs uppercase tracking-[0.2em] text-zinc-300">
                {n.label}
              </a>
            ))}
            <a href="#demo" onClick={() => setMenuOpen(false)} className="block text-amber-400 font-mono text-xs uppercase tracking-[0.15em]">
              Try one free estimate
            </a>
            <a href={REGISTER_URL} className="block rounded-lg bg-amber-500 text-center py-2.5 font-heading text-sm font-bold text-[#0B0C0E]">
              Start free trial
            </a>
          </div>
        )}
      </header>

      <main id="top">
        {/* Hero */}
        <section className="relative mx-auto max-w-6xl px-5 sm:px-8 pt-14 sm:pt-20 pb-16 sm:pb-24">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
            <div>
              <p className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-400 mb-6">
                <Sparkle size={12} weight="fill" /> From voice to paid — jobspoken.com
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-[3.4rem] font-extrabold tracking-tight leading-[1.05]">
                Speak the job.{" "}
                <span className="text-amber-400">Price the work.</span>{" "}
                Send the bill.
              </h1>
              <p className="mt-5 max-w-xl text-base sm:text-lg text-zinc-400 leading-relaxed">
                Speak the job. JobSpoken maps the materials and labor, fills prices from live lookups or estimates, and gives you an instant estimate.
                The client e-signs on their phone, you get a job-won email and an in-app alert, you schedule the work, then invoice and get paid faster.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#demo"
                  data-testid="hero-cta-demo"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-amber-500 px-6 py-3.5 font-heading text-sm font-bold text-[#0B0C0E] hover:bg-amber-400 transition-colors"
                >
                  <Microphone size={18} weight="fill" /> Try one free estimate
                </a>
                <a
                  href={REGISTER_URL}
                  data-testid="hero-cta-signup"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 px-6 py-3.5 font-heading text-sm font-bold text-zinc-100 hover:border-amber-500/50 hover:text-amber-300 transition-colors"
                >
                  Start 14-day free trial
                </a>
              </div>
              <p className="mt-4 text-xs text-zinc-500">One free estimate on this page · 14-day trial, no card · Then $14.99/mo or $119.99/yr</p>
            </div>
            <LiveVoiceDemo />
          </div>
        </section>

        <TradeGallery />

        {/* From voice to paid */}
        <section id="how-it-works" className="relative border-t border-white/5 py-20 sm:py-28" data-testid="how-it-works-section">
          <div className="mx-auto max-w-6xl px-5 sm:px-8">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400 mb-4">02 — From voice to paid</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-3xl">
              Speak it once. Walk out with a billable draft.
            </h2>
            <p className="mt-4 max-w-2xl text-sm sm:text-base text-zinc-400 leading-relaxed">
              No more napkin math and late-night invoices. Talk through the job, let prices fill in, send an e-signable estimate,
              schedule the win, then invoice and collect — often the same day.
            </p>
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {VOICE_TO_PAID.map((s) => (
                <div key={s.n} className="rounded-2xl border border-white/10 p-6 bg-gradient-to-b from-white/[0.04] to-transparent">
                  <div className="font-mono text-amber-400 text-sm tracking-[0.2em] mb-4">{s.n}</div>
                  <h3 className="font-heading text-xl font-semibold">{s.title}</h3>
                  <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <SavingsSection />

        {/* Win more jobs */}
        <section id="win-jobs" className="relative border-t border-white/5 py-20 sm:py-28">
          <div className="mx-auto max-w-6xl px-5 sm:px-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500/15 text-amber-400">
                <Trophy size={18} weight="bold" />
              </div>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400">04 — Win more jobs</p>
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight max-w-2xl">
              Be the contractor who answers with a number — not a maybe.
            </h2>
            <p className="mt-4 max-w-2xl text-sm sm:text-base text-zinc-400 leading-relaxed">
              Automatic prices, phone e-signatures, and instant job-won alerts while you&apos;re still on site close more work
              than a follow-up that never leaves your notes app.
            </p>
            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              {WIN_JOBS.map((item) => (
                <BenefitCard key={item.title} item={item} />
              ))}
            </div>
          </div>
        </section>

        {/* Finish faster & better */}
        <section id="do-the-work" className="relative border-t border-white/5 py-20 sm:py-28">
          <div className="mx-auto max-w-6xl px-5 sm:px-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500/15 text-amber-400">
                <HardHat size={18} weight="bold" />
              </div>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400">05 — Finish faster &amp; better</p>
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight max-w-2xl">
              Keep the job moving — costs, stock, and proof in one place.
            </h2>
            <p className="mt-4 max-w-2xl text-sm sm:text-base text-zinc-400 leading-relaxed">
              Less hunting for receipts, less guessing what&apos;s left on the van, more time swinging a hammer
              (or a screwdriver, or a manometer).
            </p>
            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              {DO_THE_WORK.map((item) => (
                <BenefitCard key={item.title} item={item} />
              ))}
            </div>
          </div>
        </section>

        {/* Get paid sooner */}
        <section id="get-paid" className="relative border-t border-white/5 py-20 sm:py-28">
          <div className="mx-auto max-w-6xl px-5 sm:px-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500/15 text-amber-400">
                <Wallet size={18} weight="bold" />
              </div>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400">06 — Get paid days to weeks sooner</p>
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight max-w-2xl">
              Invoice on site. Collect before you hit the next driveway.
            </h2>
            <p className="mt-4 max-w-2xl text-sm sm:text-base text-zinc-400 leading-relaxed">
              Same-day invoices and card payment links beat mailing a paper statement and waiting on the float.
              Reminders handle the stragglers.
            </p>
            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              {GET_PAID.map((item) => (
                <BenefitCard key={item.title} item={item} />
              ))}
            </div>
          </div>
        </section>

        <ClientExperience />

        {/* Features */}
        <section id="features" className="relative border-t border-white/5 py-20 sm:py-28" data-testid="features-section">
          <div className="mx-auto max-w-6xl px-5 sm:px-8">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400 mb-4">08 — Built for the trades</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-2xl">
              Everything between the job and the paycheck — not just the invoice.
            </h2>
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {FEATURES.map((f) => (
                <div
                  key={f.title}
                  data-testid={`feature-card-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="rounded-2xl border border-white/10 bg-[#13151A]/80 p-5 hover:border-amber-500/40 transition-colors"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/15 text-amber-400 mb-4">
                    <f.icon size={20} weight="bold" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-zinc-50">{f.title}</h3>
                  <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{f.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <VoicesSection />

        {/* Pricing */}
        <section id="pricing" className="relative border-t border-white/5 py-20 sm:py-28" data-testid="pricing-section">
          <div className="mx-auto max-w-6xl px-5 sm:px-8">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400 mb-4">10 — Pricing</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Simple Pro plan. Real trial.</h2>
            <div className="mt-10 grid md:grid-cols-2 gap-5 max-w-3xl">
              <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-400">Monthly</p>
                <p className="mt-3 font-heading text-4xl font-extrabold">$14.99<span className="text-lg text-zinc-400 font-semibold">/mo</span></p>
                <p className="mt-2 text-sm text-zinc-400">Billed monthly after your 14-day free trial.</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-[#13151A] p-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Yearly</p>
                <p className="mt-3 font-heading text-4xl font-extrabold">$119.99<span className="text-lg text-zinc-400 font-semibold">/yr</span></p>
                <p className="mt-2 text-sm text-zinc-400">Best value — about two months free vs monthly.</p>
              </div>
            </div>
            <ul className="mt-8 space-y-2 text-sm text-zinc-400 max-w-xl">
              {[
                "14-day free trial, no card required to start",
                "Voice intake with automatic Live lookup / Estimated pricing",
                "E-signable estimates, job-won alerts, and built-in schedule",
                "Branded PDFs, payment links, receipts, properties, stock",
                "Founding beta pricing, with a founding code, is $9.99/mo or $99.99/yr",
                "Cancel anytime from Settings or your app store",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <CheckCircle size={18} weight="fill" className="text-amber-400 shrink-0 mt-0.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={REGISTER_URL} className="inline-flex rounded-xl bg-amber-500 px-6 py-3.5 font-heading text-sm font-bold text-[#0B0C0E] hover:bg-amber-400">
                Create free account
              </a>
              <a href={LOGIN_URL} className="inline-flex rounded-xl border border-white/15 px-6 py-3.5 font-heading text-sm font-bold text-zinc-200 hover:border-amber-500/40">
                Sign in
              </a>
            </div>
          </div>
        </section>

        {/* Stores */}
        <section className="relative border-t border-white/5 py-16">
          <div className="mx-auto max-w-6xl px-5 sm:px-8">
            <h2 className="font-heading text-xl font-bold">Get the app</h2>
            <p className="mt-2 text-sm text-zinc-400 max-w-lg">Use the web app today. Native iOS and Android builds are coming soon.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button type="button" disabled className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-5 py-3 text-sm text-zinc-400 cursor-not-allowed">
                <AppleLogo size={20} weight="fill" /> App Store — coming soon
              </button>
              <button type="button" disabled className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-5 py-3 text-sm text-zinc-400 cursor-not-allowed">
                <GooglePlayLogo size={20} weight="fill" /> Google Play — coming soon
              </button>
              <a href={REGISTER_URL} className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-[#0B0C0E] hover:bg-amber-400">
                Open web app
              </a>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="relative border-t border-white/5 py-20 sm:py-28">
          <div className="mx-auto max-w-3xl px-5 sm:px-8">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400 mb-4">11 — FAQ</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-8">Questions from the field.</h2>
            {FAQS.map((item, i) => (
              <FaqItem key={item.q} item={item} open={faqOpen === i} onToggle={() => setFaqOpen(faqOpen === i ? -1 : i)} />
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="relative border-t border-white/5 py-20">
          <div className="mx-auto max-w-3xl px-5 sm:px-8 text-center">
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">
              Your next invoice is <span className="text-amber-400">one sentence away.</span>
            </h2>
            <p className="mt-4 text-zinc-400">Join Job Spoken free for 14 days. Speak a job before you leave the driveway.</p>
            <a href={REGISTER_URL} className="mt-8 inline-flex rounded-xl bg-amber-500 px-8 py-4 font-heading text-base font-bold text-[#0B0C0E] hover:bg-amber-400">
              Start free trial
            </a>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/5 py-10">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 flex flex-col sm:flex-row gap-6 sm:items-center sm:justify-between">
          <div className="flex items-center gap-2.5">
            <LogoMark className="h-8 w-8" />
            <div>
              <div className="font-heading text-sm font-bold uppercase">Job Spoken</div>
              <div className="text-xs text-zinc-500">© {year} Job Spoken. All rights reserved.</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 text-sm text-zinc-400">
            <a href="#features" className="hover:text-amber-400">Features</a>
            <a href="#pricing" className="hover:text-amber-400">Pricing</a>
            <a href={LOGIN_URL} className="hover:text-amber-400">Sign in</a>
            <Link to="/privacy" className="hover:text-amber-400">Privacy</Link>
            <Link to="/terms" className="hover:text-amber-400">Terms</Link>
            <a href="/images/trades/CREDITS.md" className="hover:text-amber-400">Photo credits</a>
            <a href="/privacy.html" className="sr-only">Privacy HTML</a>
            <a href="/terms.html" className="sr-only">Terms HTML</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
